import { signal } from '@preact/signals-core';
import { activeRoleSignal, getAuthHeaders, customersSignal, searchCustomersForAutocomplete, sitesSignal } from './assets';

export const departmentsSignal = signal<any[]>([]);
export const selectedLocationCustomerIdSignal = signal<string>('');
export const selectedSiteIdSignal = signal<string | null>(null);
export const selectedDepartmentIdSignal = signal<string | null>(null);

export async function refreshSites() {
	try {
		const res = await fetch('/api/v1/sites', { headers: getAuthHeaders() });
		if (res.ok) {
			sitesSignal.value = await res.json();
		}
	} catch (e) {
		console.error('Failed to fetch sites', e);
	}
}

export async function refreshDepartments() {
	const customerId = selectedLocationCustomerIdSignal.value;
	if (!customerId) {
		departmentsSignal.value = [];
		return;
	}
	try {
		const res = await fetch(`/api/v1/departments?customerId=${customerId}`, { headers: getAuthHeaders() });
		if (res.ok) {
			departmentsSignal.value = await res.json();
		} else {
			departmentsSignal.value = [];
		}
	} catch (e) {
		console.error('Failed to fetch departments', e);
	}
}

export function renderLocationsPage(): string {
	const role = activeRoleSignal.value;
	const isReadOnly = role === 'technician' || role === 'viewer';
	const customers = customersSignal.value || [];
	const sites = sitesSignal.value || [];
	const departments = departmentsSignal.value || [];

	// Ensure selected customer ID is initialized
	if (!selectedLocationCustomerIdSignal.value && customers.length > 0) {
		selectedLocationCustomerIdSignal.value = customers[0].id;
	}

	const selectedCustId = selectedLocationCustomerIdSignal.value;
	const selectedCustomer = customers.find(c => c.id === selectedCustId);
	const customerName = selectedCustomer ? selectedCustomer.name : 'Selected Customer';

	const customerSites = sites.filter(s => s.customerId === selectedCustId);

	// Sites Table Rows
	const siteRowsHtml = customerSites.map(s => {
		const gpsStr = s.latitude && s.longitude
			? `${s.latitude.toFixed(5)}, ${s.longitude.toFixed(5)}`
			: 'N/A';
		return `
			<s-table-row>
				<s-table-cell><strong>${s.name}</strong></s-table-cell>
				<s-table-cell><span style="font-family:monospace; font-size:0.85rem;">${gpsStr}</span></s-table-cell>
				<s-table-cell>
						${!isReadOnly ? `
							<s-button id="btn-edit-site-${s.id}" size="slim">Edit</s-button>
							<s-button id="btn-delete-site-${s.id}" tone="critical" size="slim">Delete</s-button>
						` : `<s-text tone="subdued">Read-only</s-text>`}
				</s-table-cell>
			</s-table-row>
		`;
	}).join('');

	// Map Site IDs to Name
	const siteMap = new Map<string, string>(sites.map(s => [s.id, s.name]));

	// Departments Table Rows
	const deptRowsHtml = departments.map(d => {
		const assignedSites = (d.siteIds || []).map((id: string) => siteMap.get(id) || 'Unknown Site');
		const badgesHtml = assignedSites.length === 0
			? '<s-badge tone="attention">No Sites Assigned</s-badge>'
			: assignedSites.map((name: string) => `<s-badge>${name}</s-badge>`).join(' ');

		return `
			<s-table-row>
				<s-table-cell><strong>${d.name}</strong></s-table-cell>
				<s-table-cell>
					<div style="display:flex; flex-wrap:wrap; gap:0.25rem;">
						${badgesHtml}
					</div>
				</s-table-cell>
				<s-table-cell>
					<s-button-group>
						${!isReadOnly ? `
							<s-button id="btn-edit-dept-${d.id}" size="slim">Edit</s-button>
							<s-button id="btn-delete-dept-${d.id}" tone="critical" size="slim">Delete</s-button>
						` : `<s-text tone="subdued">Read-only</s-text>`}
					</s-button-group>
				</s-table-cell>
			</s-table-row>
		`;
	}).join('');



	// Site Selection Checkboxes for Create Modal
	const createSiteCheckboxesHtml = customerSites.map(s => `
		<s-checkbox label="${s.name}" id="create-dept-site-${s.id}" value="${s.id}"></s-checkbox>
	`).join('');

	return `
		<s-page heading="Locations & Departments" subtitle="Manage sites and assign departments." inlineSize="large">
			<!-- Customer Select Header -->
			<s-section>
				<s-box padding="base" background="surface" border="base" borderRadius="base">
					<div style="display:flex; flex-direction:column; gap:0.25rem; max-width: 320px;">
						<opm-autocomplete
							id="location-customer-autocomplete"
							label="Select Customer Context"
							placeholder="Search customers..."
							selected-id="${selectedCustId}"
						></opm-autocomplete>
					</div>
				</s-box>
			</s-section>

			<!-- Double Column Split Section -->
			<s-grid gridTemplateColumns="1fr 1fr" gap="base">
				<!-- Sites Card -->
				<s-box padding="none" background="surface" border="base" borderRadius="base">
					<div style="padding: 1rem; border-bottom: 1px solid var(--p-color-border-subdued); display:flex; justify-content:space-between; align-items:center;">
						<s-heading element="h3">Sites (${customerSites.length})</s-heading>
						${!isReadOnly ? `<s-button variant="primary" size="slim" id="btn-open-create-site">Add Site</s-button>` : ''}
					</div>
					<div style="padding: 0;">
						${customerSites.length === 0 ? `
							<div style="padding: 3rem; text-align: center;">
								<s-text tone="subdued">No sites registered for this customer.</s-text>
							</div>
						` : `
							<s-table>
								<s-table-header-row>
									<s-table-header>Site Name</s-table-header>
									<s-table-header>GPS Coordinates</s-table-header>
									<s-table-header>Actions</s-table-header>
								</s-table-header-row>
								<s-table-body>
									${siteRowsHtml}
								</s-table-body>
							</s-table>
						`}
					</div>
				</s-box>

				<!-- Departments Card -->
				<s-box padding="none" background="surface" border="base" borderRadius="base">
					<div style="padding: 1rem; border-bottom: 1px solid var(--p-color-border-subdued); display:flex; justify-content:space-between; align-items:center;">
						<s-heading element="h3">Departments (${departments.length})</s-heading>
						${!isReadOnly ? `<s-button variant="primary" size="slim" id="btn-open-create-dept">Add Department</s-button>` : ''}
					</div>
					<div style="padding: 0;">
						${departments.length === 0 ? `
							<div style="padding: 3rem; text-align: center;">
								<s-text tone="subdued">No departments configured for this customer.</s-text>
							</div>
						` : `
							<s-table>
								<s-table-header-row>
									<s-table-header>Department Name</s-table-header>
									<s-table-header>Assigned Sites</s-table-header>
									<s-table-header>Actions</s-table-header>
								</s-table-header-row>
								<s-table-body>
									${deptRowsHtml}
								</s-table-body>
							</s-table>
						`}
					</div>
				</s-box>
			</s-grid>

			<!-- Create Site Modal -->
			<s-modal id="create-site-modal" heading="Create New Site for ${customerName}">
				<s-form id="form-create-site">
					<s-stack gap="base">
						<s-text-field label="Site Name" name="name" placeholder="e.g. Headquarters" required></s-text-field>
						<s-number-field label="Latitude" name="latitude" placeholder="e.g. 45.4215" step="0.00001"></s-number-field>
						<s-number-field label="Longitude" name="longitude" placeholder="e.g. -75.6972" step="0.00001"></s-number-field>
						<div id="create-site-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-create-site">Create Site</s-button>
							<s-button commandFor="create-site-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Edit Site Modal -->
			<s-modal id="edit-site-modal" heading="Edit Site Details">
				<s-form id="form-edit-site">
					<s-stack gap="base">
						<s-text-field label="Site Name" name="name" id="edit-site-name-input" required></s-text-field>
						<s-number-field label="Latitude" name="latitude" id="edit-site-latitude-input" step="0.00001"></s-number-field>
						<s-number-field label="Longitude" name="longitude" id="edit-site-longitude-input" step="0.00001"></s-number-field>
						<div id="edit-site-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-edit-site">Save Changes</s-button>
							<s-button commandFor="edit-site-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Delete Site Modal -->
			<s-modal id="delete-site-modal" heading="Delete Site?">
				<s-stack gap="base">
					<s-text tone="critical">Are you sure you want to delete this site? Deleting is permanent and will soft-delete the site.</s-text>
					<div id="delete-site-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
					<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
						<s-button variant="primary" tone="critical" id="btn-confirm-delete-site">Delete Site</s-button>
						<s-button commandFor="delete-site-modal" command="--hide">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>

			<!-- Create Department Modal -->
			<s-modal id="create-dept-modal" heading="Create New Department for ${customerName}">
				<s-form id="form-create-dept">
					<s-stack gap="base">
						<s-text-field label="Department Name" name="name" placeholder="e.g. IT Department" required></s-text-field>
						
						<s-heading element="h4">Assign Sites</s-heading>
						<div style="max-height:200px; overflow-y:auto; display:flex; flex-direction:column; gap:0.25rem; border:1px solid var(--p-color-border-subdued); padding:0.5rem; border-radius:4px;">
							${customerSites.length === 0 ? '<s-text tone="subdued">Create sites first to assign them.</s-text>' : createSiteCheckboxesHtml}
						</div>

						<div id="create-dept-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-create-dept">Create Department</s-button>
							<s-button commandFor="create-dept-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Edit Department Modal -->
			<s-modal id="edit-dept-modal" heading="Edit Department Details">
				<s-form id="form-edit-dept">
					<s-stack gap="base">
						<s-text-field label="Department Name" name="name" id="edit-dept-name-input" required></s-text-field>
						
						<s-heading element="h4">Assign Sites</s-heading>
						<div id="edit-dept-sites-container" style="max-height:200px; overflow-y:auto; display:flex; flex-direction:column; gap:0.25rem; border:1px solid var(--p-color-border-subdued); padding:0.5rem; border-radius:4px;">
							<!-- Checked checkboxes rendered dynamically -->
						</div>

						<div id="edit-dept-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
						<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
							<s-button variant="primary" id="btn-submit-edit-dept">Save Changes</s-button>
							<s-button commandFor="edit-dept-modal" command="--hide">Cancel</s-button>
						</s-stack>
					</s-stack>
				</s-form>
			</s-modal>

			<!-- Delete Department Modal -->
			<s-modal id="delete-dept-modal" heading="Delete Department?">
				<s-stack gap="base">
					<s-text tone="critical">Are you sure you want to delete this department? Deleting will soft-delete the department and unlink it from all sites.</s-text>
					<div id="delete-dept-error" style="display:none; color:var(--p-color-text-critical); margin-top:0.5rem; font-size:0.875rem;"></div>
					<s-stack direction="inline" gap="small" style="margin-top:1.5rem;">
						<s-button variant="primary" tone="critical" id="btn-confirm-delete-dept">Delete Department</s-button>
						<s-button commandFor="delete-dept-modal" command="--hide">Cancel</s-button>
					</s-stack>
				</s-stack>
			</s-modal>
		</s-page>
	`;
}

export function setupLocationsPageListeners() {
	const customerId = selectedLocationCustomerIdSignal.value;
	if (!customerId) return;

	const customerSites = (sitesSignal.value || []).filter(s => s.customerId === customerId);
	const departments = departmentsSignal.value || [];

	// 1. Customer Context Change Listener (Autocomplete)
	const custAutocomplete = document.getElementById('location-customer-autocomplete');
	if (custAutocomplete) {
		(custAutocomplete as any).loadItems = searchCustomersForAutocomplete;
		custAutocomplete.addEventListener('select', async (e: any) => {
			selectedLocationCustomerIdSignal.value = e.detail.id;
			await refreshDepartments();
			triggerAppReRender();
		});
	}

	// 2. Open Create Site Modal
	const openCreateSiteBtn = document.getElementById('btn-open-create-site');
	if (openCreateSiteBtn) {
		openCreateSiteBtn.addEventListener('click', () => {
			const modal = document.getElementById('create-site-modal') as any;
			const errDiv = document.getElementById('create-site-error');
			if (errDiv) errDiv.style.display = 'none';
			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
		});
	}

	// 3. Submit Create Site Form
	const submitCreateSiteBtn = document.getElementById('btn-submit-create-site');
	if (submitCreateSiteBtn) {
		submitCreateSiteBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const form = document.getElementById('form-create-site');
			if (!form) return;

			const name = (form.querySelector('[name="name"]') as any)?.value || '';
			const latVal = (form.querySelector('[name="latitude"]') as any)?.value || '';
			const lngVal = (form.querySelector('[name="longitude"]') as any)?.value || '';

			const body: any = {
				customerId: customerId,
				name: name,
			};

			if (latVal) body.latitude = Number(latVal);
			if (lngVal) body.longitude = Number(lngVal);

			const errDiv = document.getElementById('create-site-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch('/api/v1/sites', {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify(body),
				});

				if (res.ok) {
					const modal = document.getElementById('create-site-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshSites();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to create site.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 4. Attach Site Edit & Delete Row Actions
	customerSites.forEach(s => {
		const editBtn = document.getElementById(`btn-edit-site-${s.id}`);
		if (editBtn) {
			editBtn.addEventListener('click', () => {
				selectedSiteIdSignal.value = s.id;

				const nameInput = document.getElementById('edit-site-name-input') as HTMLInputElement;
				if (nameInput) nameInput.value = s.name;

				const latInput = document.getElementById('edit-site-latitude-input') as HTMLInputElement;
				if (latInput) latInput.value = s.latitude ? s.latitude.toString() : '';

				const lngInput = document.getElementById('edit-site-longitude-input') as HTMLInputElement;
				if (lngInput) lngInput.value = s.longitude ? s.longitude.toString() : '';

				const errDiv = document.getElementById('edit-site-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('edit-site-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const deleteBtn = document.getElementById(`btn-delete-site-${s.id}`);
		if (deleteBtn) {
			deleteBtn.addEventListener('click', () => {
				selectedSiteIdSignal.value = s.id;
				const errDiv = document.getElementById('delete-site-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('delete-site-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}
	});

	// 5. Submit Edit Site Form
	const submitEditSiteBtn = document.getElementById('btn-submit-edit-site');
	if (submitEditSiteBtn) {
		submitEditSiteBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const id = selectedSiteIdSignal.value;
			if (!id) return;

			const form = document.getElementById('form-edit-site');
			if (!form) return;

			const name = (form.querySelector('[name="name"]') as any)?.value || '';
			const latVal = (form.querySelector('[name="latitude"]') as any)?.value || '';
			const lngVal = (form.querySelector('[name="longitude"]') as any)?.value || '';

			const body: any = {
				name: name,
			};

			if (latVal) body.latitude = Number(latVal);
			if (lngVal) body.longitude = Number(lngVal);

			const errDiv = document.getElementById('edit-site-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/sites/${id}`, {
					method: 'PUT',
					headers: getAuthHeaders(),
					body: JSON.stringify(body),
				});

				if (res.ok) {
					const modal = document.getElementById('edit-site-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshSites();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to update site.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 6. Confirm Delete Site Action
	const confirmDeleteSiteBtn = document.getElementById('btn-confirm-delete-site');
	if (confirmDeleteSiteBtn) {
		confirmDeleteSiteBtn.addEventListener('click', async () => {
			const id = selectedSiteIdSignal.value;
			if (!id) return;

			const errDiv = document.getElementById('delete-site-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/sites/${id}`, {
					method: 'DELETE',
					headers: getAuthHeaders(),
				});

				if (res.ok) {
					const modal = document.getElementById('delete-site-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshSites();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to delete site.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 7. Open Create Department Modal
	const openCreateDeptBtn = document.getElementById('btn-open-create-dept');
	if (openCreateDeptBtn) {
		openCreateDeptBtn.addEventListener('click', () => {
			const modal = document.getElementById('create-dept-modal') as any;
			const errDiv = document.getElementById('create-dept-error');
			if (errDiv) errDiv.style.display = 'none';
			if (modal && typeof modal.showOverlay === 'function') {
				modal.showOverlay();
			}
		});
	}

	// 8. Submit Create Department Form
	const submitCreateDeptBtn = document.getElementById('btn-submit-create-dept');
	if (submitCreateDeptBtn) {
		submitCreateDeptBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const form = document.getElementById('form-create-dept');
			if (!form) return;
			const name = (form.querySelector('[name="name"]') as any)?.value || '';

			// Gather checked sites
			const checkedSiteIds: string[] = [];
			customerSites.forEach(s => {
				const cb = document.getElementById(`create-dept-site-${s.id}`) as any;
				if (cb && cb.checked) {
					checkedSiteIds.push(s.id);
				}
			});

			const errDiv = document.getElementById('create-dept-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch('/api/v1/departments', {
					method: 'POST',
					headers: getAuthHeaders(),
					body: JSON.stringify({
						customerId: customerId,
						name: name,
						siteIds: checkedSiteIds,
					}),
				});

				if (res.ok) {
					const modal = document.getElementById('create-dept-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshDepartments();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to create department.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 9. Attach Department Edit & Delete Actions
	departments.forEach(d => {
		const editBtn = document.getElementById(`btn-edit-dept-${d.id}`);
		if (editBtn) {
			editBtn.addEventListener('click', () => {
				selectedDepartmentIdSignal.value = d.id;

				const nameInput = document.getElementById('edit-dept-name-input') as HTMLInputElement;
				if (nameInput) nameInput.value = d.name;

				const errDiv = document.getElementById('edit-dept-error');
				if (errDiv) errDiv.style.display = 'none';

				// Dynamically render the checkboxes inside the container to avoid HTML caching issues
				const container = document.getElementById('edit-dept-sites-container');
				if (container) {
					container.innerHTML = customerSites.map(s => {
						const isChecked = (d.siteIds || []).includes(s.id);
						return `
							<s-checkbox label="${s.name}" id="edit-dept-site-${s.id}" value="${s.id}" ${isChecked ? 'checked' : ''}></s-checkbox>
						`;
					}).join('');
				}

				const modal = document.getElementById('edit-dept-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}

		const deleteBtn = document.getElementById(`btn-delete-dept-${d.id}`);
		if (deleteBtn) {
			deleteBtn.addEventListener('click', () => {
				selectedDepartmentIdSignal.value = d.id;
				const errDiv = document.getElementById('delete-dept-error');
				if (errDiv) errDiv.style.display = 'none';

				const modal = document.getElementById('delete-dept-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}
	});

	// 10. Submit Edit Department Form
	const submitEditDeptBtn = document.getElementById('btn-submit-edit-dept');
	if (submitEditDeptBtn) {
		submitEditDeptBtn.addEventListener('click', async (e) => {
			e.preventDefault();
			const id = selectedDepartmentIdSignal.value;
			if (!id) return;

			const form = document.getElementById('form-edit-dept');
			if (!form) return;
			const name = (form.querySelector('[name="name"]') as any)?.value || '';

			// Gather checked sites
			const checkedSiteIds: string[] = [];
			customerSites.forEach(s => {
				const cb = document.getElementById(`edit-dept-site-${s.id}`) as any;
				if (cb && cb.checked) {
					checkedSiteIds.push(s.id);
				}
			});

			const errDiv = document.getElementById('edit-dept-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/departments/${id}`, {
					method: 'PUT',
					headers: getAuthHeaders(),
					body: JSON.stringify({
						name: name,
						siteIds: checkedSiteIds,
					}),
				});

				if (res.ok) {
					const modal = document.getElementById('edit-dept-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshDepartments();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to update department.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}

	// 11. Confirm Delete Department Action
	const confirmDeleteDeptBtn = document.getElementById('btn-confirm-delete-dept');
	if (confirmDeleteDeptBtn) {
		confirmDeleteDeptBtn.addEventListener('click', async () => {
			const id = selectedDepartmentIdSignal.value;
			if (!id) return;

			const errDiv = document.getElementById('delete-dept-error');
			if (errDiv) errDiv.style.display = 'none';

			try {
				const res = await fetch(`/api/v1/departments/${id}`, {
					method: 'DELETE',
					headers: getAuthHeaders(),
				});

				if (res.ok) {
					const modal = document.getElementById('delete-dept-modal') as any;
					if (modal && typeof modal.hideOverlay === 'function') {
						modal.hideOverlay();
					}
					await refreshDepartments();
					triggerAppReRender();
				} else {
					const data = await res.json();
					if (errDiv) {
						errDiv.textContent = data.error || 'Failed to delete department.';
						errDiv.style.display = 'block';
					}
				}
			} catch (err) {
				if (errDiv) {
					errDiv.textContent = 'Network failure.';
					errDiv.style.display = 'block';
				}
			}
		});
	}
}

function triggerAppReRender() {
	const ev = new HashChangeEvent('hashchange');
	window.dispatchEvent(ev);
}
